import type { JobResponse } from '~/types/hlx/jobResponse';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { postData } from '~/utils/service';
import { ExistingInquiryError } from './initialInquiryReceipt';
import { MARKET_ACTION_TIMEOUT_MS } from './requestPolicy';

export type InitialInquiryTarget =
  | { listingType: 'JOB'; jobListingId: number }
  | { listingType: 'MSAAS'; msaasListingId: number; providerIds?: number[] }
  | { listingType: 'SERVICE'; providerServiceListingId: number };

export type InitialInquiryInput = InitialInquiryTarget & { messageBody: string };

const snapshotInput = (input: InitialInquiryInput): InitialInquiryInput => {
  const messageBody = input.messageBody;
  switch (input.listingType) {
    case 'JOB':
      return { listingType: 'JOB', jobListingId: input.jobListingId, messageBody };
    case 'MSAAS':
      return {
        listingType: 'MSAAS',
        msaasListingId: input.msaasListingId,
        messageBody,
        ...(input.providerIds ? { providerIds: [...input.providerIds] } : {}),
      };
    case 'SERVICE':
      return {
        listingType: 'SERVICE',
        providerServiceListingId: input.providerServiceListingId,
        messageBody,
      };
  }
};

/** One explicit create-or-reuse attempt; callers own uncertainty and any deliberate replay. */
export const sendInitialInquiry = (
  input: InitialInquiryInput,
  signal?: AbortSignal
): Promise<JobResponse> => {
  const data = snapshotInput(input);
  return withRequestDeadline(
    async (requestSignal) => {
      const result = await postData<{
        jobResponse: JobResponse;
        createdMessage: boolean;
        message: { id: number; messageBody: string } | null;
      }>('jobResponseInitialMessage', {
        data,
        signal: requestSignal,
      });
      const response = result?.jobResponse;
      if (!response || !Number.isInteger(response.id) || response.id <= 0) {
        throw new Error('Unable to complete response. Please try again.');
      }
      const matchesListing =
        data.listingType === 'JOB'
          ? response.jobListingId === data.jobListingId
          : data.listingType === 'MSAAS'
            ? response.msaasListingId === data.msaasListingId
            : response.providerServiceListingId === data.providerServiceListingId;
      if (response.listingType !== data.listingType || !matchesListing) {
        throw new Error('Initial inquiry response scope changed.');
      }
      if (result.createdMessage !== true) {
        if (result.createdMessage !== false || typeof result.message?.messageBody !== 'string') {
          throw new Error('Initial inquiry message acknowledgement is incomplete.');
        }
        if (result.message.messageBody !== data.messageBody) {
          throw new ExistingInquiryError(response);
        }
      }
      return response;
    },
    MARKET_ACTION_TIMEOUT_MS,
    signal
  );
};
